import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { supabase } from '../../../lib/supabase';

const CreateAccountModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    companyName: '',
    planType: 'free',
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData?.email?.trim()) {
      setError('Email is required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!formData?.password || formData?.password?.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (formData?.password !== formData?.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    // ── Step 1: Save the current admin session tokens ──────────────────────
    let adminAccessToken = null;
    let adminRefreshToken = null;
    let adminUserId = null;

    try {
      const { data: { session: adminSession } } = await supabase?.auth?.getSession();
      adminAccessToken = adminSession?.access_token || null;
      adminRefreshToken = adminSession?.refresh_token || null;
      adminUserId = adminSession?.user?.id || null;
    } catch (sessionErr) {
      console.warn('Could not read admin session:', sessionErr?.message);
    }

    try {
      // ── Step 2: Create the new user ──────────────────────────────────────
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email: formData?.email?.trim()?.toLowerCase(),
        password: formData?.password,
        options: {
          data: {
            full_name: formData?.fullName?.trim() || null,
            company_name: formData?.companyName?.trim() || null,
            plan_type: formData?.planType,
          },
          emailRedirectTo: `${window.location?.origin}/authentication`,
        },
      });

      if (signUpError) throw signUpError;

      const newUserId = data?.user?.id;

      // ── Step 3: Upsert user_profiles row (trigger may have already done it) ──
      if (newUserId) {
        const { error: profileError } = await supabase?.from('user_profiles')?.upsert(
            {
              id: newUserId,
              email: formData?.email?.trim()?.toLowerCase(),
              full_name: formData?.fullName?.trim() || null,
              company_name: formData?.companyName?.trim() || null,
              plan_type: formData?.planType,
              tier: formData?.planType,
              subscription_status: 'active',
              updated_at: new Date()?.toISOString(),
            },
            { onConflict: 'id' }
          );

        if (profileError) {
          // Non-fatal — trigger may have already created the row
          console.warn('Profile upsert warning:', profileError?.message);
        }
      }

      // ── Step 4: Restore admin session ────────────────────────────────────
      if (adminAccessToken && adminRefreshToken) {
        try {
          const { data: { session: currentSession } } = await supabase?.auth?.getSession();
          const adminWasLoggedOut =
            !currentSession || currentSession?.user?.id !== adminUserId;

          if (adminWasLoggedOut) {
            const { error: restoreError } = await supabase?.auth?.setSession({
              access_token: adminAccessToken,
              refresh_token: adminRefreshToken,
            });

            if (restoreError) {
              // Try refresh as fallback
              const { error: refreshError } = await supabase?.auth?.refreshSession({
                refresh_token: adminRefreshToken,
              });
              if (refreshError) {
                console.warn('Session restore failed:', refreshError?.message);
                setError(
                  'Account created, but admin session was lost. Please log in again.'
                );
                setLoading(false);
                return;
              }
            }
          }
        } catch (restoreErr) {
          console.warn('Session restore exception:', restoreErr?.message);
        }
      }

      // ── Step 5: Show success and close ───────────────────────────────────
      setSuccess(
        `Account created for ${formData?.email}. ${
          data?.user?.email_confirmed_at
            ? '' :'A confirmation email has been sent.'
        }`
      );

      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 1800);
    } catch (err) {
      console.error('CreateAccountModal error:', err);
      const msg = err?.message || '';
      if (msg?.toLowerCase()?.includes('already registered') || msg?.toLowerCase()?.includes('already exists')) {
        setError('An account with this email already exists.');
      } else {
        setError(msg || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg shadow-elevation-lg w-full max-w-lg mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="UserPlus" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground">
                Create New Account
              </h3>
              <p className="text-xs text-muted-foreground">
                Add a new user account to the platform
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="X" size={22} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              <Icon name="AlertCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          {success && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-md bg-green-50 border border-green-200 text-green-700 text-sm">
              <Icon name="CheckCircle" size={16} className="mt-0.5 flex-shrink-0" />
              <span>{success}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Full Name"
              placeholder="John Smith"
              value={formData?.fullName}
              onChange={(e) => handleChange('fullName', e?.target?.value)}
            />
            <Input
              label="Company Name"
              placeholder="Acme Corp"
              value={formData?.companyName}
              onChange={(e) => handleChange('companyName', e?.target?.value)}
            />
          </div>

          <Input
            label="Email Address *"
            type="email"
            placeholder="user@example.com"
            value={formData?.email}
            onChange={(e) => handleChange('email', e?.target?.value)}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Password *"
              type="password"
              placeholder="Min. 6 characters"
              value={formData?.password}
              onChange={(e) => handleChange('password', e?.target?.value)}
              required
            />
            <Input
              label="Confirm Password *"
              type="password"
              placeholder="Repeat password"
              value={formData?.confirmPassword}
              onChange={(e) => handleChange('confirmPassword', e?.target?.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">
              Plan Type
            </label>
            <select
              value={formData?.planType}
              onChange={(e) => handleChange('planType', e?.target?.value)}
              className="w-full border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              <option value="free">Free</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
              <option value="demo">Demo (Full Features)</option>
            </select>
            {formData?.planType === 'demo' && (
              <p className="mt-1.5 text-xs text-amber-600 flex items-center gap-1">
                <span>⚠</span>
                Demo accounts have full feature access — for screenshots and demonstrations only.
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={loading} disabled={loading}>
              <Icon name="UserPlus" size={16} className="mr-2" />
              Create Account
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateAccountModal;
